import { useCallback, useContext, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { getCategoryFriendlyNameByID } from '@sbt-web/category-utils';
import { OptimizelySubitoContext, userIdPrefix } from '@sbt-web/houston-wrapper';
import { getBuyerInfo, searchRecommended, sendAdReply, sendFirstContact } from '@sbt-web/network/client';
import { HTTPStatusCode, NetError, CategoryID, AdvertiserType, ItemURN, AdTypes, FilterWithValues, InfoBuyerAdReply } from '@sbt-web/network/types';
import { urnTransformer } from '@sbt-web/utils';
import { HADES_PATH } from '@/shared/constants';
import { adTypesToSlugMap } from '@/tools/search/values';
import { clickRecommendationSearchEvent, sendFormButtonClick, sendMessageSuccessEvent, sendMessageSuccessGTMEvent, viewRecommendationSearchEvent } from '../../../tracking';
import type { ErrorCode, SearchData, FormProps, TrackingSendFormData } from '../../../types';
import { ERRORS_CODE, getError } from '../utils/errors';
import { isUserEligibleForStreamAdReplyRollout as isUserEligibleForStreamAdReplyRolloutUtil } from '../../../utils';
import { useMessageValidation } from './useMessageValidation';
const dispatchSuccessEvent = ({
  isPhoneTrack,
  forwardPhone,
  phone,
  search
}: {
  isPhoneTrack: boolean;
  forwardPhone?: boolean;
  phone: string;
  search?: SearchData;
}) => {
  window.dispatchEvent(new CustomEvent('subito:AdReply:Success', {
    detail: {
      forwardPhone,
      isPhoneTrack,
      phone,
      search,
      disabled: true
    }
  }));
};
export function buildRecommendedUrlFromFilter(filters: FilterWithValues[]): string {
  const filterMap = filters.reduce((map: Record<string, FilterWithValues>, filter) => ({
    ...map,
    [filter.uri]: filter
  }), {});
  const place = filterMap['/geo/region']?.values[0].metadata?.friendly_name;
  const adTypeKey = filterMap['/type']?.values[0].key as AdTypes;
  const adType = adTypesToSlugMap[adTypeKey];
  const category = getCategoryFriendlyNameByID(filterMap['/category']?.values[0].key as CategoryID);
  const recommendationUrl = new URL(`${process.env.NEXT_PUBLIC_ENV_BASE_URL}/annunci-${place}/${adType}/${category}/`);
  const advType = filterMap['/advertiser_type']?.values[0].key;
  const carBrand = filterMap['/car/brand']?.values[0].key;
  const carModel = filterMap['/car/model']?.values[0].key;
  const priceMin = filterMap['/price/min']?.values[0].key;
  const priceMax = filterMap['/price/max']?.values[0].key;
  if (advType) recommendationUrl.searchParams.set('advt', advType);
  if (carBrand) recommendationUrl.searchParams.set('cb', carBrand);
  if (carModel) recommendationUrl.searchParams.set('cm', carModel);
  if (priceMin) recommendationUrl.searchParams.set('ps', priceMin);
  if (priceMax) recommendationUrl.searchParams.set('pe', priceMax);
  return recommendationUrl.toString();
}
export function useContactForm({
  onCloseContactForm,
  isPro,
  category,
  enableAttachments,
  userId,
  advertiserName,
  subject,
  adUrn,
  onSendReplyCallback,
  requestLogin,
  buyerName,
  search,
  item
}: FormProps, formType: 'default' | 'pro' = 'default') {
  const shouldRenderPhoneForwarder = useMemo(() => isPro && category !== CategoryID.OfferteLavoro, [isPro, category]);
  const [buyerPhone, setBuyerPhone] = useState<string | undefined>(undefined);
  const [buyerPhoneForMessage, setBuyerPhoneForMessage] = useState<string>();
  const [curriculum, setCurriculum] = useState<Blob | undefined>(undefined);
  const initialMessage = `Ciao ${advertiserName}, ti contatto per l'annuncio ${subject}, `;
  const [message, setMessage] = useState<string>(initialMessage);
  const [replyDone, setReplyDone] = useState<boolean>(false);
  const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);
  const {
    errorMessage,
    isValid
  } = useMessageValidation();
  const [errors, setErrors] = useState<string[]>([]);
  const submitRef = useRef<boolean>(false);
  const {
    optimizely
  } = useContext(OptimizelySubitoContext);
  const [recommendationSearch, setRecommendationSearch] = useState<{
    count: number;
    url: string;
  }>();
  const selectedInterests = useRef<Set<string>>(new Set());
  const updateSelectedInterest = useCallback((value: string, isSelected: boolean) => {
    if (isSelected) {
      selectedInterests.current.add(value);
    } else {
      selectedInterests.current.delete(value);
    }
  }, []);
  const checkRelated = useCallback(async (urn: ItemURN) => {
    try {
      const res = await searchRecommended(process.env.NEXT_PUBLIC_HADES_BASE_URL, urn);
      const {
        count,
        filters
      } = res.body;
      if (!count) return;
      const url = buildRecommendedUrlFromFilter(filters);
      setRecommendationSearch({
        count,
        url
      });
    } catch {
      return;
    }
  }, []);
  const handleSetPhoneNumber = (phoneNumber: string) => {
    setBuyerPhone(phoneNumber);
    setBuyerPhoneForMessage(phoneNumber);
  };
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!submitRef.current && isValid(message)) {
      submitRef.current = true;
      sendAdreply(userId);
    }
  };
  const sendDoneTracking = (buyerInfo: InfoBuyerAdReply) => {
    const isEditMessage = initialMessage.trim() !== message.trim();
    const trackingData: TrackingSendFormData = {
      cv: enableAttachments ? !!curriculum : null,
      phone: shouldRenderPhoneForwarder ? !!buyerInfo.phone : null,
      editText: isEditMessage
    };
    sendFormButtonClick(trackingData);
  };
  const buildBuyerInfo = (buyerId: string, hermesBuyerInfo?: InfoBuyerAdReply): InfoBuyerAdReply => {
    const name = buyerId === userId ? buyerName ?? hermesBuyerInfo?.name ?? '' : hermesBuyerInfo?.name ?? '';
    return {
      ...hermesBuyerInfo,
      userId: buyerId,
      email: hermesBuyerInfo?.email ?? '',
      name,
      forwardPhone: buyerPhone !== undefined ? !!buyerPhone : hermesBuyerInfo?.forwardPhone,
      phone: buyerPhone ?? hermesBuyerInfo?.phone
    };
  };
  const trackSendAdReply = () => {
    optimizely?.onReady().then(() => {
      optimizely.track('Send_Message');
    });
  };
  const sendAdreplyToStreamOrMessaging = async (buyerInfo_0: InfoBuyerAdReply, message_0: string) => {
    const isUserEligibleForStreamAdReplyRollout = await isUserEligibleForStreamAdReplyRolloutUtil(userId, optimizely);
    if (isUserEligibleForStreamAdReplyRollout) {
      const itemIdInfo = urnTransformer(adUrn);
      await sendFirstContact(process.env.NEXT_PUBLIC_HADES_BASE_URL, itemIdInfo?.adId ?? '', userId, message_0, buyerInfo_0, curriculum);
      submitRef.current = false;
      handleSuccessRequest(buyerInfo_0);
    } else {
      const responseSendAdReply = await sendAdReply(HADES_PATH, adUrn, message_0, {
        ...buyerInfo_0
      }, curriculum);
      submitRef.current = false;
      if (responseSendAdReply?.status === HTTPStatusCode.OK) {
        handleSuccessRequest(buyerInfo_0);
      }
    }
  };
  const sendAdreply = async (buyerId_0: string) => {
    let buyerInfo_1, getBuyerInfoResponse;
    try {
      getBuyerInfoResponse = await getBuyerInfo(HADES_PATH, buyerId_0);
    } catch (error) {
      if (error instanceof NetError && error?.cause.status !== HTTPStatusCode.NotFound) {
        setShowErrorDialog(true);
      }
    }
    try {
      buyerInfo_1 = buildBuyerInfo(buyerId_0, getBuyerInfoResponse?.body?.buyerInfo);
      const bodyMessage = buildMessage(message, formType === 'pro' ? Array.from(selectedInterests.current) : [], buyerPhoneForMessage);
      await sendAdreplyToStreamOrMessaging(buyerInfo_1, bodyMessage);
    } catch (error_0) {
      if (error_0 instanceof NetError) {
        switch (error_0.cause.status) {
          case HTTPStatusCode.BadRequest:
            handleBadRequest(error_0.cause);
            break;
          case HTTPStatusCode.OK:
            if (buyerInfo_1) handleSuccessRequest(buyerInfo_1);
            break;
          case HTTPStatusCode.Unauthorized:
            {
              handleUnauthorized();
              break;
            }
          case HTTPStatusCode.PayloadTooLarge:
            {
              handlePayloadTooLarge();
              break;
            }
          case HTTPStatusCode.TooManyRequests:
            {
              handleRateLimiter();
              break;
            }
          default:
            setShowErrorDialog(true);
            break;
        }
      }
    }
  };
  const handleBadRequest = (payload: unknown) => {
    if (payload && typeof payload === 'object' && 'errors' in payload) {
      const sendAdReplyErrors = (payload.errors as ErrorCode[]).map(error_1 => error_1.error_code);
      const isErrorWithLabel = getError(ERRORS_CODE, sendAdReplyErrors);
      if (isErrorWithLabel) {
        setErrors(sendAdReplyErrors);
      } else {
        setShowErrorDialog(true);
      }
    }
  };
  const handlePayloadTooLarge = () => {
    setErrors(['REPLY:too-large-request']);
  };
  const handleRateLimiter = () => {
    setErrors(['REPLY:throttled']);
    setShowErrorDialog(true);
  };
  const handleSuccessRequest = (buyerInfo_2: InfoBuyerAdReply) => {
    setReplyDone(true);
    trackSendAdReply();
    if (userId) {
      // Used only by Face2Face ABtest
      optimizely?.track('SendMessage', `${userIdPrefix}${userId}`, {
        urn: item.urn
      });
    }
    dispatchSuccessEvent({
      forwardPhone: buyerInfo_2.forwardPhone,
      isPhoneTrack: shouldRenderPhoneForwarder,
      phone: buyerInfo_2.phone ?? '',
      search
    });
    sendDoneTracking(buyerInfo_2);
    sendMessageSuccessGTMEvent();
    sendMessageSuccessEvent({
      item,
      forwardPhone: buyerInfo_2.forwardPhone,
      isPhoneTrack: shouldRenderPhoneForwarder,
      phone: buyerInfo_2.phone ?? '',
      search
    }, formType === 'pro' ? {
      selectedInterests: Array.from(selectedInterests.current),
      changedDefaultText: initialMessage.trim() !== message.trim()
    } : undefined);
    onSendReplyCallback?.();
  };
  const handleUnauthorized = async () => {
    const {
      id
    } = await requestLogin();
    if (id) {
      sendAdreply(id);
    }
  };
  const handleConfirmMessageCtaClick = () => {
    if (!recommendationSearch) {
      onCloseContactForm();
    } else {
      clickRecommendationSearchEvent(recommendationSearch.count);
      window.location.assign(recommendationSearch.url);
    }
  };
  useEffect(() => {
    if (recommendationSearch && replyDone) {
      viewRecommendationSearchEvent(recommendationSearch.count);
    }
  }, [replyDone, recommendationSearch]);
  useEffect(() => {
    if (item.category.id === CategoryID.Auto && item.advertiser.type === AdvertiserType.Private) {
      checkRelated(item.urn);
    }
  }, [item, checkRelated]);
  useEffect(() => {
    let didCancel = false;
    (async () => {
      try {
        const response = await getBuyerInfo(HADES_PATH, userId);
        if (!didCancel && response?.body?.buyerInfo) {
          setBuyerPhone(response.body.buyerInfo.phone);
        }
      } catch (e_0) {
        console.error(e_0);
      }
    })();
    return () => {
      didCancel = true;
    };
  }, [userId, buyerName]);
  return {
    shouldRenderPhoneForwarder,
    buyerPhone,
    curriculum,
    setCurriculum,
    initialMessage,
    message,
    setMessage,
    replyDone,
    setReplyDone,
    showErrorDialog,
    setShowErrorDialog,
    errorMessage,
    isValid,
    errors,
    setErrors,
    submitRef,
    recommendationSearch,
    handleSetPhoneNumber,
    handleSubmit,
    handleConfirmMessageCtaClick,
    sendAdreply,
    updateSelectedInterest
  };
}
export const buildMessage = (message: string, selected: string[], phone?: string) => {
  let fullMessage = message.trim();
  if (selected.length > 0) {
    fullMessage = `${fullMessage} Posso avere informazioni su: ${selected.join('; ')}`;
  }
  if (phone) {
    fullMessage = `${fullMessage} puoi contattarmi al ${phone}`;
  }
  return fullMessage;
};